/**
 * Work orders — the mission layer's data and its evaluator.
 *
 * A work order is what a technician actually receives: a briefing, a building
 * in some state, and the expectation of evidence. Three rules keep this layer
 * honest, and all three are structural rather than aspirational:
 *
 * THE ORDER NEVER TOUCHES THE PHYSICS. Setup is limited to arming existing,
 * harness-validated faults and house events by id. If a lesson needs a world
 * state no validated fault produces, the fault gets built and harnessed first
 * and the order second.
 *
 * COMPLETION IS DERIVED, NOT DECLARED. An objective is met when the journal
 * contains the evidence. The evaluator below is a pure function over the
 * journal slice since the order was accepted; mission code holds no flags the
 * world did not earn.
 *
 * NO PAR, NO AGGREGATE SCORE. shift.ts refuses both, for reasons that apply
 * doubly here: scoring against an optimum punishes investigation, and folding
 * downtime and scrap into one number hides the only interesting trade. An
 * order completes or it does not; the debrief itemises what it cost.
 *
 * CRITERIA ARE DATA, NOT CLOSURES. Each objective is a typed matcher, so the
 * missions harness can prove referential integrity (every id points at a real
 * fault, tool or reading) and completability (the intended path's synthetic
 * journal completes; an empty one does not) without running a browser.
 *
 * ENGLISH STAYS IN THE .ts, following the fault-text convention: the titles,
 * briefings and debriefs here are the source of record, and the catalogues
 * carry translations keyed as mission.<id>.* — present wins, missing falls
 * back to what is already here.
 */

import type { JournalEvent } from './Journal'

/** One piece of expected evidence, as a matcher over journal events. */
export type Criterion =
  /** The student opened this tool's panel. */
  | { kind: 'visit'; tool: string }
  /** An earned reading was taken at this tool (optionally a specific one). */
  | { kind: 'reading'; tool: string; readingId?: string }
  /** The diagnosis was committed at this tool - correctly, if required. A
   *  list means any of them counts: one building event can be called from
   *  every tool it reaches. */
  | { kind: 'call'; tool: string | string[]; correct?: true }
  /** A maintenance action was performed, e.g. 'house:upw' at a skid. */
  | { kind: 'maintenance'; action: string }
  /** The armed house event was cleared - the building recovered. */
  | { kind: 'houseCleared' }
  /** The student went down the stairs. */
  | { kind: 'place'; where: 'subfab' }
  /** The lot was handed on from this station. `from` is an asset tag. */
  | { kind: 'handoff'; from: string }
  /** A unit of product finished at this station, in spec. Asset tag. */
  | { kind: 'product'; tool: string; inSpec: true }
  /** The student chose a policy on the line - for the chiller trip, the queue-time window, the
   *  excursion's sampling rate or the maintenance. Any choice counts: the order is about making
   *  the decision with the picture in front of you, and the debrief says what each one cost.
   *  An event without a scenario is the chiller trip, from before the others existed. */
  | { kind: 'lineDecision'; scenario?: string }

export interface Objective {
  id: string
  /** English label; catalogues override via mission.<mission>.objective.<id>. */
  label: string
  criterion: Criterion
}

export interface Mission {
  id: string
  track: 'room' | 'tool' | 'building' | 'capstone'
  /** The world when the order is picked up. Ids only - never new physics. */
  setup: { house?: string; toolFault?: { tool: string; fault: string } }
  title: string
  briefing: string
  debrief: string
  objectives: Objective[]
}

/**
 * Which objectives the journal slice satisfies.
 *
 * Objectives are independent - each may be met by any event in the slice -
 * except that the slice itself starts when the order was accepted, so nothing
 * done beforehand counts. `houseCleared` needs one extra input because the
 * bus's state is not a journal event: the caller says whether the house is
 * healthy NOW and whether this order armed something to clear.
 */
export function evaluate(
  mission: Mission,
  events: JournalEvent[],
  houseHealthyNow: boolean,
): { met: boolean[]; done: boolean } {
  const met = mission.objectives.map((o) => {
    const c = o.criterion
    switch (c.kind) {
      case 'visit':
        return events.some((e) => e.kind === 'visit' && e.tool === c.tool)
      case 'reading':
        return events.some(
          (e) =>
            e.kind === 'reading' &&
            e.toolId === c.tool &&
            (c.readingId === undefined || e.readingId === c.readingId),
        )
      case 'call': {
        const tools = Array.isArray(c.tool) ? c.tool : [c.tool]
        return events.some(
          (e) =>
            e.kind === 'call' &&
            tools.includes(e.toolId ?? '') &&
            (c.correct === undefined || e.correct),
        )
      }
      case 'maintenance':
        return events.some((e) => e.kind === 'maintenance' && e.action === c.action)
      case 'houseCleared':
        // Evidence in two halves: the repair era ended (bus healthy now) and
        // it ended during this order (a maintenance event exists in-slice).
        return houseHealthyNow && events.some((e) => e.kind === 'maintenance')
      case 'place':
        return events.some((e) => e.kind === 'place' && e.where === c.where)
      case 'handoff':
        return events.some((e) => e.kind === 'handoff' && e.from === c.from)
      case 'product':
        return events.some((e) => e.kind === 'product' && e.tool === c.tool && e.inSpec)
      case 'lineDecision':
        return events.some((e) => e.kind === 'lineDecision' && (c.scenario === undefined || (e.scenario ?? 'chiller') === c.scenario))
    }
  })
  return { met, done: met.every(Boolean) }
}

/**
 * The orders: three teaching tracks plus the capstone, every one riding
 * machinery other harnesses already validate. The capstone landed only after
 * the route was proven playable end to end - its evidence is the actual
 * handoff and product events the four substrate stations record, and the
 * ORDER of those events needs no enforcement here because the physics
 * enforces it: an ingot cannot be epitaxied and a wafer cannot be sliced.
 */
export const MISSIONS: Mission[] = [
  // ------------------------------------------------------------- track: room
  {
    id: 'holdTheClass',
    track: 'room',
    setup: {},
    title: 'Hold the class',
    briefing:
      'Routine certification. Show the bay holds its ISO class where it matters — at the ' +
      'product, not at the wall monitor. The portable counter costs an hour; that is what it ' +
      'is for.',
    debrief:
      'The wall monitor is bolted where the wall is, and what matters is the air a metre ' +
      'above open product. You paid an hour to know the difference. That habit is the room.',
    objectives: [
      { id: 'enter', label: 'Enter the bay and open GOWN-00', criterion: { kind: 'visit', tool: 'gowning' } },
      { id: 'workzone', label: 'Run the portable counter at the work zone', criterion: { kind: 'reading', tool: 'gowning', readingId: 'workZone' } },
    ],
  },
  {
    id: 'theWallLies',
    track: 'room',
    setup: { toolFault: { tool: 'gowning', fault: 'wornGarments' } },
    title: 'The wall lies',
    briefing:
      'Defect density is creeping on lots that pass through this bay, and the wall monitor ' +
      'reads comfortably in class. Someone is wrong. Find out who, and say so.',
    debrief:
      'The garments were past their laundering life — a source the room ventilation never ' +
      'gets a chance to dilute before it reaches the wafer. The wall reads the room; the ' +
      'wafer reads the person standing over it.',
    objectives: [
      { id: 'workzone', label: 'Measure where the product is', criterion: { kind: 'reading', tool: 'gowning', readingId: 'workZone' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'gowning', correct: true } },
    ],
  },
  {
    id: 'theStopwatch',
    track: 'room',
    setup: { house: 'ahuBeltSlip' },
    title: 'The stopwatch',
    briefing:
      'The bay reads dirtier at the same headcount and takes visibly longer to settle after ' +
      'a door event. The ceiling filters were changed last month. Diagnose it — and be ready ' +
      'to defend the reading you trusted.',
    debrief:
      'Recovery time is set by air changes and nothing else, so a slow recovery with a clean ' +
      'filter bank is the building, not the room. The instrument that caught it was a ' +
      'stopwatch. The repair is not on this floor — but that is another work order.',
    objectives: [
      { id: 'recovery', label: 'Time the recovery', criterion: { kind: 'reading', tool: 'gowning', readingId: 'recovery' } },
      { id: 'call', label: 'Call the escalation, correctly', criterion: { kind: 'call', tool: 'gowning', correct: true } },
    ],
  },

  // ------------------------------------------------------------- track: tool
  {
    id: 'firstShift',
    track: 'tool',
    setup: { toolFault: { tool: 'clean', fault: 'spentSc1' } },
    title: 'First shift at the bench',
    briefing:
      'Defect density at inspection has doubled on everything through CLN-09. The bench has ' +
      'not alarmed and looks exactly as it always does. Your first diagnosis: earn a reading ' +
      'before you call it.',
    debrief:
      'One chemistry out, one fine — the signature of a spent bath, and nothing about a ' +
      'transparent liquid ever looks spent. You paid for the monitor wafer and it paid you ' +
      'back.',
    objectives: [
      { id: 'visit', label: 'Take the shift at CLN-09', criterion: { kind: 'visit', tool: 'clean' } },
      { id: 'reading', label: 'Earn at least one reading', criterion: { kind: 'reading', tool: 'clean' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'clean', correct: true } },
    ],
  },
  {
    id: 'invisibleFault',
    track: 'tool',
    setup: { toolFault: { tool: 'clean', fault: 'spentSc2' } },
    title: 'The invisible fault',
    briefing:
      'Gate oxide integrity is failing on one product and nobody can find a defect. Particle ' +
      'counts are perfect. Whatever this is, the cheap measurements cannot see it.',
    debrief:
      'A monolayer of copper is invisible to every optical inspector in the building, and it ' +
      'rides into the furnace on wafers that look perfect. The expensive, destructive reading ' +
      'exists precisely because this fault does.',
    objectives: [
      { id: 'metals', label: 'Order the surface-metals measurement', criterion: { kind: 'reading', tool: 'clean', readingId: 'metalCheck' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'clean', correct: true } },
    ],
  },
  {
    id: 'shipsAnyway',
    track: 'tool',
    setup: { toolFault: { tool: 'epi', fault: 'spentSusceptor' } },
    title: 'Ships anyway',
    briefing:
      'Nothing has been reported against EPI-20. Every visit is in specification. The order ' +
      'exists because the qualification data looks a shade off trend, and someone upstairs ' +
      'trusts your judgement more than the spec limits.',
    debrief:
      'The hardest shift on the floor: a broken tool shipping passing wafers. Nothing forced ' +
      'the call you just made — which is exactly why it was worth making.',
    objectives: [
      { id: 'reading', label: 'Earn a reading at EPI-20', criterion: { kind: 'reading', tool: 'epi' } },
      { id: 'call', label: 'Call it anyway, correctly', criterion: { kind: 'call', tool: 'epi', correct: true } },
    ],
  },

  {
    id: 'innocentInstrument',
    track: 'tool',
    setup: { toolFault: { tool: 'metrology', fault: 'wrongIndex' } },
    title: 'The innocent instrument',
    briefing:
      'MET-05 reads the oxide 4% high and half the fab wants it recalibrated. Before you take ' +
      'a working gauge down: a repeatable measurement of the wrong assumption looks exactly ' +
      'like a broken gauge, and only one reading can tell them apart.',
    debrief:
      'The reflectometer was innocent — the FILM changed. The deposition tool drifted and the ' +
      'measurement recipe went on assuming the old material, so a perfect fit of the wrong ' +
      'model reported a thicker film, repeatably. The ellipsometer, which solves for the index ' +
      'instead of assuming it, is what separated "gauge broken" from "world changed" — and a ' +
      'recalibration would have buried the real drift under a corrected lie.',
    objectives: [
      { id: 'ellipsometer', label: 'Cross-check on the ellipsometer', criterion: { kind: 'reading', tool: 'metrology', readingId: 'ellipsometer' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'metrology', correct: true } },
    ],
  },

  // ------------------------------------------------- track: tool, one per bay
  //
  // The four orders above were written first and go deep: each is a lookalike
  // problem where the interesting move is ruling something OUT. The sixteen
  // below cover the rest of the floor, one order per tool, and they are
  // deliberately the same shape - a complaint, the one earned reading that
  // resolves it, and a call.
  //
  // WHY UNIFORM RATHER THAN CLEVER. A student who has run the four above knows
  // the method; what they have not seen is that it is the SAME method at a
  // wire saw, a mould press and an excess-flow valve. Sixteen variations on
  // one shape teach that transfer better than sixteen novelties would, and
  // every one still rides a fault some other harness already validates.
  //
  // Each briefing is a complaint with no diagnosis in it, each debrief names
  // the truth and what the lookalike would have cost. The reading each order
  // asks for is an EARNED one wherever the tool has a discriminating one -
  // the point of the layer is that evidence costs something.
  {
    id: 'oxygenClimb',
    track: 'tool',
    setup: { toolFault: { tool: 'crystal', fault: 'spentCrucible' } },
    title: 'The oxygen climb',
    briefing:
      'Device engineering is rejecting wafers from the last three ingots for interstitial ' +
      'oxygen, and the puller has not had a recipe change in a month. Same pull rate, same ' +
      'rotation, same argon setpoint. Find out what is different.',
    debrief:
      'The silica crucible was near the end of its life. A crucible dissolves into the melt ' +
      'the whole time it is hot, and a tired one gives up oxygen faster than a fresh one at ' +
      'exactly the recipe that worked last month. The argon flow was nominal, which is what ' +
      'rules out the sweep — the other way oxygen gets high — and turning the argon up would ' +
      'have masked it for one more ingot.',
    objectives: [
      { id: 'oxygen', label: 'Measure interstitial oxygen in the last boule', criterion: { kind: 'reading', tool: 'crystal', readingId: 'oxygenCheck' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'crystal', correct: true } },
    ],
  },
  {
    id: 'flatAndSlow',
    track: 'tool',
    setup: { toolFault: { tool: 'waferprep', fault: 'glazedPad' } },
    title: 'Flat, and slow',
    briefing:
      'Polish is missing its cycle time on every lot and the operators have been adding minutes ' +
      'to hit thickness. Flatness still passes. Nobody has changed the slurry or the down-force.',
    debrief:
      'The pad was glazed — its surface loaded and stopped cutting, so removal rate fell and ' +
      'the fix everyone reached for, more time, was buying the number by spending the tool. ' +
      'Slurry size was in spec, which rules out the abrasive; a glazed pad and a spent slurry ' +
      'both slow the rate, and only one of them is fixed by a conditioning cut.',
    objectives: [
      { id: 'rate', label: 'Measure the removal rate on a monitor', criterion: { kind: 'reading', tool: 'waferprep', readingId: 'removalRate' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'waferprep', correct: true } },
    ],
  },
  {
    id: 'sharpEnoughToPass',
    track: 'tool',
    setup: { toolFault: { tool: 'litho', fault: 'focusDrift' } },
    title: 'Sharp enough to pass',
    briefing:
      'CD is drifting at LITH-03 and the obvious answer is dose. Before you touch the source: ' +
      'a scanner that has lost focus prints the same CD error a dose drift does, and the two ' +
      'call for opposite corrections.',
    debrief:
      'The stage had lost focus, not the source. Defocus costs CD and it costs CONTRAST and ' +
      'NILS with it — a dose error moves the size and leaves the edge alone, which is what ' +
      'the aerial-image numbers separate. Correcting this with dose would have held the ' +
      'number while the process window kept closing underneath it.',
    objectives: [
      { id: 'focus', label: 'Run the focus probe', criterion: { kind: 'reading', tool: 'litho', readingId: 'focusprobe' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'litho', correct: true } },
    ],
  },
  {
    id: 'whereThePhotonsWent',
    track: 'tool',
    setup: { toolFault: { tool: 'euv', fault: 'dropletTimingDrift' } },
    title: 'Where the photons went',
    briefing:
      'EUV-22 is a quarter slow and the night shift has a thirty-hour collector swap pencilled ' +
      'in for tomorrow. An EUV scanner can lose its light in three places, and only one of them ' +
      'is the collector. Walk the beam path before anyone opens the source vessel.',
    debrief:
      'The light was never made. The main laser pulse was landing late on the droplet, so the ' +
      'plasma gave about 60% of its qualified pulse energy, and power was down by the same ' +
      'fraction everywhere after it. A collector sits AFTER the plasma: it cannot lower the ' +
      'pulse energy measured at the plasma, and that one reading is what saved thirty hours ' +
      'and a collector that was doing its job.',
    objectives: [
      { id: 'timing', label: 'Run the droplet-to-pulse timing scan', criterion: { kind: 'reading', tool: 'euv', readingId: 'dropletTiming' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'euv', correct: true } },
    ],
  },
  {
    id: 'theRulerIsWide',
    track: 'tool',
    setup: { toolFault: { tool: 'ebeam', fault: 'gassyTip' } },
    title: 'The ruler is wide',
    briefing:
      'Every line SEM-23 has measured since the weekend reads about a nanometre wide, and ' +
      'lithography has already turned the dose up once to pull CD back. Before they do it ' +
      'again: find out whether the lines moved, or the instrument did.',
    debrief:
      'The instrument did. The cold field-emission tip had gone days without a flash, its ' +
      'energy spread had nearly tripled, and at 500 eV that is most of the spot - chromatic ' +
      'blur goes as ΔE over E. A wider spot reads every edge a little further out. The scanner ' +
      'was steered off target to correct a print that had been right, which is what a ' +
      'metrology fault costs: not a scrapped wafer, a wrong decision somewhere else.',
    objectives: [
      { id: 'spread', label: 'Measure the gun’s energy spread', criterion: { kind: 'reading', tool: 'ebeam', readingId: 'energySpread' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'ebeam', correct: true } },
    ],
  },
  {
    id: 'coldPlateWarmQubit',
    track: 'tool',
    setup: { toolFault: { tool: 'quantum', fault: 'missingAttenuator' } },
    title: 'Cold plate, warm qubit',
    briefing:
      'QDT-24 reached 10 mK on schedule and every thermometer on it is normal. The chip in it ' +
      'measured well in another fridge last month and reads terribly here. Somebody will ' +
      'propose warming up to swap the chip. Find out what the qubit can hear first.',
    debrief:
      'A 20 dB attenuator was missing from the drive line, so the line carried a hundred times ' +
      'the room-temperature noise it should: an eighth of a photon in the qubit’s mode, a qubit ' +
      'at 109 mK on a plate at 10. The chip was fine and so was the fridge. A qubit is not at ' +
      'the temperature of the metal it is bolted to - it is at the temperature of its wiring - ' +
      'and the only thermometer that reads that is the qubit itself.',
    objectives: [
      { id: 'line', label: 'Measure the drive line’s insertion loss', criterion: { kind: 'reading', tool: 'quantum', readingId: 'lineInsertionLoss' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'quantum', correct: true } },
    ],
  },
  {
    id: 'fiftyDegreesHot',
    track: 'tool',
    setup: { toolFault: { tool: 'thermal', fault: 'hotAnneal' } },
    title: 'Fifty degrees nobody set',
    briefing:
      'Sheet resistance out of THRM-04 is running low and junctions are coming out deep. The ' +
      'implanter group has been asked to check their dose, and they are not pleased about it. ' +
      'Before IMP-10 loses a shift to a dose audit: a low dose does not do this.',
    debrief:
      'The RTP pyrometer was reading low, so the lamps were driven fifty degrees past the ' +
      'setpoint the screen showed. Diffusivity is Arrhenius: fifty degrees nearly doubles the ' +
      'Dt, the dopant spreads, the junction deepens and the sheet resistance FALLS. A short ' +
      'dose moves sheet resistance the other way and leaves the junction where it was. The ' +
      'direction of the error named the tool before anybody measured anything.',
    objectives: [
      { id: 'pyrometer', label: 'Check the pyrometer against a calibration wafer', criterion: { kind: 'reading', tool: 'thermal', readingId: 'pyrometer' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'thermal', correct: true } },
    ],
  },
  {
    id: 'theSlowLeak',
    track: 'tool',
    setup: { toolFault: { tool: 'etch', fault: 'chamberLeak' } },
    title: 'The slow leak',
    briefing:
      'ETCH-01 is not reaching base pressure and the shift before you logged it as a tired ' +
      'pump. Pumps are a four-hour swap and there is one spare in the building, so be sure ' +
      'before you spend it.',
    debrief:
      'A real leak, not a tired pump. Both raise base pressure and both look identical on the ' +
      'gauge; what separates them is where the gas is coming from — a leak admits atmosphere ' +
      'at a constant rate, and pump speed was fine. The spare pump is still on the shelf, ' +
      'which is the entire value of having checked.',
    objectives: [
      { id: 'seal', label: 'Run the seal check', criterion: { kind: 'reading', tool: 'etch', readingId: 'sealcheck' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'etch', correct: true } },
    ],
  },
  {
    id: 'theCupThatLies',
    track: 'tool',
    setup: { toolFault: { tool: 'implant', fault: 'driftedCup' } },
    title: 'The cup that lies',
    briefing:
      'Sheet resistance is coming back low on implanted lots and the implanter says every dose ' +
      'was on target. One of those two statements is wrong, and the tool is the one with an ' +
      'opinion about itself.',
    debrief:
      'The Faraday cup had drifted, so the tool was counting fewer ions than it delivered and ' +
      'over-implanting to make its number. A dose monitor that measures itself cannot catch ' +
      'this — only a wafer can, which is why sheet resistance is the reading that settles it. ' +
      'Every other bench number on the tool was nominal, and every one of them was innocent.',
    objectives: [
      { id: 'rho', label: 'Implant a monitor and measure sheet resistance', criterion: { kind: 'reading', tool: 'implant', readingId: 'sheetRho' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'implant', correct: true } },
    ],
  },
  {
    id: 'thePitchThatWalks',
    track: 'tool',
    setup: { toolFault: { tool: 'patterning', fault: 'thinSidewallFilm' } },
    title: 'The pitch that walks',
    briefing:
      'PAT-21 is passing mean CD and failing at electrical test, with the failures alternating ' +
      'line to line. One exposure made these lines, so overlay cannot be the answer.',
    debrief:
      'Pitch walking. The spacer film came in thin, and in a self-aligned quadruple cell the ' +
      'spacer thickness IS the half pitch — so the four lines the module makes from one ' +
      'mandrel are no longer evenly spaced. The mean is untouched, which is why CD passed, ' +
      'and no amount of scanner work would have helped: this is a deposition problem wearing ' +
      'a lithography complaint.',
    objectives: [
      { id: 'walk', label: 'Measure the pitch, line by line', criterion: { kind: 'reading', tool: 'patterning', readingId: 'pitchWalk' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'patterning', correct: true } },
    ],
  },
  {
    id: 'dishingOut',
    track: 'tool',
    setup: { toolFault: { tool: 'cmp', fault: 'agedSlurry' } },
    title: 'Dishing out',
    briefing:
      'CMP-11 is over-polishing the wide copper features and the pad was changed on Tuesday. ' +
      'The obvious suspect has an alibi.',
    debrief:
      'The slurry had aged and its corrosion inhibitor was spent. The inhibitor is what stops ' +
      'the chemistry attacking copper where the pad is not pressing, so without it the wide ' +
      'features dish while the narrow ones look fine — a profile signature no amount of ' +
      'down-force correction reaches. A fresh pad on old slurry would have polished faster ' +
      'into the same defect.',
    objectives: [
      { id: 'inhibitor', label: 'Assay the slurry inhibitor', criterion: { kind: 'reading', tool: 'cmp', readingId: 'inhibitor' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'cmp', correct: true } },
    ],
  },
  {
    id: 'voidsInTheVia',
    track: 'tool',
    setup: { toolFault: { tool: 'plating', fault: 'exhaustedAdditives' } },
    title: 'Voids in the via',
    briefing:
      'ECD-12 is plating to thickness and the vias are coming back with seams down the middle. ' +
      'Current density, bath temperature and contact resistance all read nominal.',
    debrief:
      'The organic additives were exhausted. Bottom-up fill is not a current setting — it is ' +
      'the accelerator and suppressor arranging where copper goes, and a bath that has lost ' +
      'them plates conformally and pinches the via shut before it fills. The thickness was ' +
      'right the whole time, which is exactly why only a cross-section could show it.',
    objectives: [
      { id: 'xsection', label: 'Cross-section a via', criterion: { kind: 'reading', tool: 'plating', readingId: 'crossSection' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'plating', correct: true } },
    ],
  },
  {
    id: 'theToolThatCriesWolf',
    track: 'tool',
    setup: { toolFault: { tool: 'inspection', fault: 'hazedOptics' } },
    title: 'The tool that cries wolf',
    briefing:
      'INSP-13 is flagging so many defects that engineering has started ignoring its maps. ' +
      'Either the line got dramatically worse this week, or the inspector did.',
    debrief:
      'The collection optics had hazed. Scattered light from a clouded lens raises the ' +
      'background the detector works against, so the tool reports defects that are not on the ' +
      'wafer — and a nuisance rate that high does more damage than a blind tool, because ' +
      'people stop reading real maps. Incoming roughness was nominal, which rules out the ' +
      'wafers themselves.',
    objectives: [
      { id: 'nuisance', label: 'Verify the nuisance rate against a known wafer', criterion: { kind: 'reading', tool: 'inspection', readingId: 'nuisance' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'inspection', correct: true } },
    ],
  },
  {
    id: 'goodDieCalledBad',
    track: 'tool',
    setup: { toolFault: { tool: 'probe', fault: 'cardOutOfPlane' } },
    title: 'Good die, called bad',
    briefing:
      'Yield at PRB-14 has dropped four points and the loss is all at the wafer edge. The tips ' +
      'were changed last week and are well inside their life.',
    debrief:
      'The probe card was out of plane. A card that is not parallel to the chuck touches down ' +
      'harder on one side than the other, so the far edge never makes proper contact and good ' +
      'die are binned as opens. Tip wear was nominal — the lookalike everyone reaches for ' +
      'first — and replacing tips would have cost a card change and fixed nothing.',
    objectives: [
      { id: 'contact', label: 'Measure contact resistance across the wafer', criterion: { kind: 'reading', tool: 'probe', readingId: 'contactResistance' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'probe', correct: true } },
    ],
  },
  {
    id: 'wideKerf',
    track: 'tool',
    setup: { toolFault: { tool: 'dicing', fault: 'wornBlade' } },
    title: 'The kerf that grew',
    briefing:
      'Assembly is losing die to edge chipping and dicing reports every lot in spec. Street ' +
      'width has not been changed and the tape lot is current.',
    debrief:
      'The blade was past its cut limit. A worn blade cuts a wider kerf and chips more at the ' +
      'edges, and the die that survive to the mould press are the ones that were not already ' +
      'cracked. Tape peel was nominal, which rules out die movement during the cut — the ' +
      'other way chipping climbs, and the one that would have sent you to the wrong supplier.',
    objectives: [
      { id: 'kerf', label: 'Measure the kerf width', criterion: { kind: 'reading', tool: 'dicing', readingId: 'kerf' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'dicing', correct: true } },
    ],
  },
  {
    id: 'theQuietDispenser',
    track: 'tool',
    setup: { toolFault: { tool: 'bonding', fault: 'starvedDispense' } },
    title: 'The quiet dispenser',
    briefing:
      'Packages from BOND-16 are failing thermal cycling and the wire bonds all pull to spec. ' +
      'The failures are under the die, not at the loops.',
    debrief:
      'The epoxy dispense was starved. Too little adhesive leaves voids under the die, and a ' +
      'void is a place heat cannot leave and stress has nowhere to go — so the part passes ' +
      'every bond test and fails the first time it is cycled. Pull strength and ball shear ' +
      'were both nominal, which is what says the wire bonder is innocent.',
    objectives: [
      { id: 'void', label: 'Image the die-attach voids', criterion: { kind: 'reading', tool: 'bonding', readingId: 'attachVoid' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'bonding', correct: true } },
    ],
  },
  {
    id: 'popcorn',
    track: 'tool',
    setup: { toolFault: { tool: 'packaging', fault: 'wetParts' } },
    title: 'Popcorn',
    briefing:
      'PKG-17 parts are cracking during solder reflow at the customer, not here. Transfer ' +
      'pressure is nominal and the compound is in date.',
    debrief:
      'The parts had taken up moisture in storage. Water trapped in a mould compound flashes ' +
      'to steam at reflow temperature and splits the package from the inside — which is why ' +
      'this fails at the customer and passes every test in this building. Mould void was ' +
      'normal, ruling out both the press and the compound, and neither of those repairs ' +
      'would have touched the dry-pack process that actually failed.',
    objectives: [
      { id: 'popcorn', label: 'Run the moisture-sensitivity reflow', criterion: { kind: 'reading', tool: 'packaging', readingId: 'popcorn' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'packaging', correct: true } },
    ],
  },
  {
    id: 'theEarlyFailures',
    track: 'tool',
    setup: { toolFault: { tool: 'finaltest', fault: 'infantMortality' } },
    title: 'The early failures',
    briefing:
      'FT-18 is failing more parts after burn-in than before it, and the tester passed its ' +
      'gauge study this morning. Handler temperature is on setpoint.',
    debrief:
      'The population itself carried infant mortality — a defect distribution from upstream, ' +
      'not a test problem. Burn-in is supposed to find these; a fallout that climbs means the ' +
      'lot arrived with them, and the answer is upstream rather than at the tester. Gauge ' +
      'sigma and handler temperature were both nominal, which is what turns "our test is ' +
      'broken" into "our wafers are", and those two go to different people.',
    objectives: [
      { id: 'fallout', label: 'Analyse the burn-in fallout', criterion: { kind: 'reading', tool: 'finaltest', readingId: 'burnInFallout' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'finaltest', correct: true } },
    ],
  },
  {
    id: 'theValveThatWont',
    track: 'tool',
    setup: { toolFault: { tool: 'gas', fault: 'seizedEfv' } },
    title: 'The valve that will not',
    briefing:
      'Routine safety verification at GAS-19. The bunker is running normally and nothing is ' +
      'alarming — which is exactly when a protective device is worth testing, because it is ' +
      'the only time you can.',
    debrief:
      'The excess-flow valve had seized open. Nothing was wrong with the gas, the scrubber or ' +
      'the extract, and nothing would have been until a line broke — at which point the one ' +
      'device meant to close would not have. A protective device that is never exercised is a ' +
      'device you are assuming, and the release model is what turns that assumption into a ' +
      'number.',
    objectives: [
      { id: 'release', label: 'Run the release model against the valve test', criterion: { kind: 'reading', tool: 'gas', readingId: 'releaseModel' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'gas', correct: true } },
    ],
  },
  {
    id: 'leakOrOutgas',
    track: 'tool',
    setup: { toolFault: { tool: 'facilities', fault: 'realLeak' } },
    title: 'Leak or outgas',
    briefing:
      'FAC-06 will not hold base and the log says the chamber was opened to atmosphere on ' +
      'Friday. Water on the walls and a real leak both do this. One of them bakes out and the ' +
      'other does not, and you cannot tell from the gauge reading.',
    debrief:
      'A real leak. The rate-of-rise SHAPE is what separates them: atmosphere entering through ' +
      'a seal climbs linearly and never stops, while water coming off the walls climbs and ' +
      'then rolls over as the surface depletes. Same base pressure, two different curves, and ' +
      'a bake-out would have cost a shift and changed nothing.',
    objectives: [
      { id: 'rise', label: 'Take the rate-of-rise curve, not just the number', criterion: { kind: 'reading', tool: 'facilities', readingId: 'riseShape' } },
      { id: 'call', label: 'Make the call, correctly', criterion: { kind: 'call', tool: 'facilities', correct: true } },
    ],
  },

  // --------------------------------------------------------- track: building
  {
    id: 'notThisTool',
    track: 'building',
    setup: { house: 'spentBed' },
    title: 'Not this tool',
    briefing:
      'CLN-09 is failing at particles AND metals in the same visit, and both baths were ' +
      'remade this morning. Before you order eight hours of bench repairs: is this bench ' +
      'even the problem?',
    debrief:
      'Failing both chemistries at once is a signature no single bath can produce — the last ' +
      'water the wafer sees is the house’s water. The repair you refused to order would ' +
      'have cost eight hours and fixed nothing.',
    objectives: [
      { id: 'phone', label: 'Call facilities for the utility panel', criterion: { kind: 'reading', tool: 'clean', readingId: 'houseutilities' } },
      { id: 'call', label: 'Call the escalation, correctly', criterion: { kind: 'call', tool: 'clean', correct: true } },
    ],
  },
  {
    id: 'takeTheStairs',
    track: 'building',
    setup: { house: 'spentBed' },
    title: 'Take the stairs',
    briefing:
      'The escalation was made: the rinse loop reads 11 MΩ·cm and the polishing mixed bed is ' +
      'the suspect. You are the facilities side of the story now. Go down and make the ' +
      'building healthy.',
    debrief:
      'The bed held 18.2 for almost its whole life and then failed across the last few ' +
      'percent — which is why the plant regenerates on throughput, never on the meter. The ' +
      'board is green; the bench upstairs recovers on its next lot.',
    objectives: [
      { id: 'descend', label: 'Take the stairs behind FAC-06', criterion: { kind: 'place', where: 'subfab' } },
      { id: 'repair', label: 'Regenerate the polishing mixed bed', criterion: { kind: 'maintenance', action: 'house:upw' } },
      { id: 'green', label: 'Leave the building healthy', criterion: { kind: 'houseCleared' } },
    ],
  },
  {
    id: 'oneEventTwoSymptoms',
    track: 'building',
    setup: { house: 'wetHeader' },
    title: 'One event, two symptoms',
    briefing:
      'Two complaints an hour apart: ALD growth is running fast and non-uniform, and the ' +
      'furnace oxide is coming out thick. Two tools, two process groups, two tickets. Visit ' +
      'both before you believe either.',
    debrief:
      'Water in the dry-gas header ruins ALD because water is its co-reactant, and thickens ' +
      'the furnace oxide by a completely different mechanism. The symptoms had nothing in ' +
      'common except a cause — and the two groups had no reason to compare notes until you ' +
      'walked between them.',
    objectives: [
      { id: 'ald', label: 'Visit the ALD tool', criterion: { kind: 'visit', tool: 'deposition' } },
      { id: 'furnace', label: 'Visit the furnace', criterion: { kind: 'visit', tool: 'thermal' } },
      { id: 'call', label: 'Call the escalation at either tool', criterion: { kind: 'call', tool: ['deposition', 'thermal'], correct: true } },
    ],
  },
  {
    id: 'twoRoomsAway',
    track: 'building',
    setup: { house: 'warmPcw' },
    title: 'Two rooms from the symptom',
    briefing:
      'It is 02:00. ETCH-01 is out of spec on selectivity and non-uniformity, its chuck temperature ' +
      'is running warm, and the same alarm is showing on every etcher in the building. Lots are ' +
      'stacking up at etch. Nothing on the etcher is broken. Find out what is, decide what the line does while ' +
      'you wait, and get the building healthy - in that order, or the line will pay for every ' +
      'hour you spend on the wrong room.',
    debrief:
      'The chiller in the sub-fab was the fault; the etcher was the symptom, two rooms away. The ' +
      'chambers came back within hours of the call - and the queue did not. Every lot that waited ' +
      'at etch arrived late at its next tool, and the line spent its spare capacity working that ' +
      'off for the rest of the day. Open the Line to see what your decision cost against the two ' +
      'you did not make: by date loses the fewest dates at this load; holding starts did nothing ' +
      'for the queue and took slack from the lots it held. WIP, throughput and cycle time are ' +
      "tied by Little's law, and a line at 95% would still be draining tomorrow.",
    objectives: [
      { id: 'etch', label: 'Go to the etcher and see the alarm', criterion: { kind: 'visit', tool: 'etch' } },
      { id: 'call', label: 'Call the escalation, correctly - it is not this tool', criterion: { kind: 'call', tool: 'etch', correct: true } },
      { id: 'decide', label: 'Decide what the line does while you wait: open the Line and choose', criterion: { kind: 'lineDecision', scenario: 'chiller' } },
      { id: 'descend', label: 'Take the stairs to the sub-fab', criterion: { kind: 'place', where: 'subfab' } },
      { id: 'repair', label: 'Restore the chilled water supply', criterion: { kind: 'maintenance', action: 'house:pcw' } },
      { id: 'green', label: 'Leave the building healthy', criterion: { kind: 'houseCleared' } },
    ],
  },
  {
    id: 'theResistClock',
    track: 'building',
    setup: { house: 'warmPcw' },
    title: 'The resist clock',
    briefing:
      'The same night, a different route: this product\'s resist has an eight-hour shelf life ' +
      'between the scanner and the etcher, and two of every etcher\'s three chambers have just ' +
      'dropped out on temperature control. Every lot the scanner exposes from now on is a lot with ' +
      'a clock running toward a chamber that is not there. Decide what the scanner does, then get ' +
      'the building healthy.',
    debrief:
      'The window was never the trip\'s problem. The lots the trip sent back were exposed before ' +
      'the chambers dropped; nothing could save them. What a window does is turn every hour of ' +
      'etch queue into rework, all day, every day - and at 95% load that is most of the line. The ' +
      'gate is what makes a window workable: hold the exposure until the etcher can eat it in ' +
      'time, at the one step whose queue costs nothing but time. It is the only hold on this ' +
      'floor that is right, and only because it holds the lot before the clock starts.',
    objectives: [
      { id: 'scanner', label: 'Go to the scanner and see what is waiting to be exposed', criterion: { kind: 'visit', tool: 'litho' } },
      { id: 'decide', label: 'Decide what the scanner does: open the Line, the queue-time window, and choose', criterion: { kind: 'lineDecision', scenario: 'qtime' } },
      { id: 'call', label: 'Call the escalation at the etcher, correctly', criterion: { kind: 'call', tool: 'etch', correct: true } },
      { id: 'descend', label: 'Take the stairs to the sub-fab', criterion: { kind: 'place', where: 'subfab' } },
      { id: 'repair', label: 'Restore the chilled water supply', criterion: { kind: 'maintenance', action: 'house:pcw' } },
      { id: 'green', label: 'Leave the building healthy', criterion: { kind: 'houseCleared' } },
    ],
  },
  {
    id: 'theSilentTool',
    track: 'tool',
    setup: { toolFault: { tool: 'deposition', fault: 'heaterReadsLow' } },
    title: 'The silent tool',
    briefing:
      'DEP-02 has been depositing thick for hours and its own screen says everything is fine, ' +
      'because the thermocouple it trusts is the thing that is wrong. Nothing on the floor knows ' +
      'yet. Metrology samples a fraction of its visits; one of those will be the lot that tells ' +
      'you. Decide how hard to sample, find the tool, and call it.',
    debrief:
      'Every lot through that tool until the inspection caught it is at risk, and that number is ' +
      'throughput through the tool times the hours it ran unseen - Little\'s law, from the yield ' +
      'side. Sampling harder buys hours off that delay and pays for them at the metrology tool, ' +
      'whose queue every lot in the building stands in. The rate is a decision about which of ' +
      'those two the fab would rather carry, and the Line shows both prices.',
    objectives: [
      { id: 'metrology', label: 'Go to metrology - it is the only tool that can see this', criterion: { kind: 'visit', tool: 'metrology' } },
      { id: 'decide', label: 'Decide the sampling rate: open the Line, the excursion, and choose', criterion: { kind: 'lineDecision', scenario: 'excursion' } },
      { id: 'call', label: 'Call the deposition tool, correctly', criterion: { kind: 'call', tool: 'deposition', correct: true } },
    ],
  },
  {
    id: 'theDueChamber',
    track: 'tool',
    setup: {},
    title: 'The due chamber',
    briefing:
      'Chamber C on ETCH-01 is due its service tonight: four planned hours, on the bottleneck, in ' +
      'a shift that is already behind. The shift lead would rather run it through the week. ' +
      'Look at the tool, then decide - and be ready to say what running on would cost.',
    debrief:
      'A planned service costs its hours and nothing else. A deferred one is a bet on a worn ' +
      'chamber, and the Line says the bet loses every week: the chamber fails, the unplanned ' +
      'repair takes twice as long, and it lands at an hour nobody chose, on the tool whose hour ' +
      'is the fab\'s hour. The lead was not wrong that four hours tonight hurts. He was wrong ' +
      'about what eight hours on Thursday would cost.',
    objectives: [
      { id: 'etch', label: 'Go to the etcher', criterion: { kind: 'visit', tool: 'etch' } },
      { id: 'decide', label: 'Decide the service: open the Line, the maintenance, and choose', criterion: { kind: 'lineDecision', scenario: 'pm' } },
    ],
  },
  {
    id: 'wrongRepair',
    track: 'building',
    setup: { house: 'ahuBeltSlip' },
    title: 'Make the building healthy',
    briefing:
      'Something in the sub-fab is sick. The order says only that. Every repair down there ' +
      'charges its hours whether or not it was the right one — the gauges are how you avoid ' +
      'paying twice.',
    debrief:
      'The gauges cleared the innocent plants before you spent a single hour on them: most ' +
      'of the sub-fab being fine is most of the diagnosis. If you repaired a healthy plant ' +
      'first, the debrief shows exactly what that certainty would have cost.',
    objectives: [
      { id: 'descend', label: 'Go down and read the gauges', criterion: { kind: 'place', where: 'subfab' } },
      { id: 'repair', label: 'Repair the plant that is actually sick', criterion: { kind: 'maintenance', action: 'house:hvac' } },
      { id: 'green', label: 'Leave the building healthy', criterion: { kind: 'houseCleared' } },
    ],
  },

  // -------------------------------------------------------------- capstone
  {
    id: 'atomsToFabs',
    track: 'capstone',
    setup: {},
    title: 'Atoms to fabs',
    briefing:
      'One lot, the whole substrate line. Pull a usable crystal at CZ-07 and hand the ingot ' +
      'on; slice it at WPR-08; run the incoming clean at CLN-09; grow the device layer at ' +
      'EPI-20, in spec, and pass the wafer to the front end. Every decision you make rides ' +
      'the travel card to the next tool - including the ones you get wrong.',
    debrief:
      'Melt to device layer: the title of this fab, walked. The wafer leaving EPI-20 carries ' +
      'your melt doping, your slice, your clean and your epitaxy on its travel card, and ' +
      'every tool downstream will live inside the budgets you just set. That is what ' +
      '"from atoms to fabs" means - not a slogan, a lot history.',
    objectives: [
      { id: 'grow', label: 'Grow a usable ingot at CZ-07', criterion: { kind: 'product', tool: 'CZ-07', inSpec: true } },
      { id: 'handCz', label: 'Hand the ingot to wafer prep', criterion: { kind: 'handoff', from: 'CZ-07' } },
      { id: 'slice', label: 'Slice, lap and polish at WPR-08', criterion: { kind: 'handoff', from: 'WPR-08' } },
      { id: 'clean', label: 'Run the incoming clean at CLN-09', criterion: { kind: 'handoff', from: 'CLN-09' } },
      { id: 'epi', label: 'Grow the device layer in spec at EPI-20', criterion: { kind: 'product', tool: 'EPI-20', inSpec: true } },
      { id: 'handEpi', label: 'Pass the finished substrate onward', criterion: { kind: 'handoff', from: 'EPI-20' } },
    ],
  },
]

export const missionById = (id: string): Mission | undefined => MISSIONS.find((m) => m.id === id)
